import { ProductAttribute, Product } from '@/types/products';
import { Stock } from '@/types/stock';
import { PUNCHOUT_CONFIG } from '@/config/punchout-config';
import { CartItem } from '@/types/cart';
import { LocationQuery } from '#vue-router';

const DEFAULT_UNSPCS_CODE = '40000000';
const STYCK_UNIT = 'STK';
/**
 * TODO: Implement lookup for UNSPCS code.
 * NOTE:
 * Company specific requirements
 *
 * 1. Cramo: return empty string
 */
export function findUNSPSCCode(companyName = '') {
  if (companyName.toUpperCase() === PUNCHOUT_CONFIG.CRAMO.COMPANY_NAME) {
    return '';
  }

  return DEFAULT_UNSPCS_CODE;
}

/**
 * NOTE:
 * Company specific requirements
 *
 * 1. Mälarenergi: Name of which category a variant belongs to, two levels up
 */
export function findMaterialGroup(companyName = '', product: Product) {
  if (companyName.toUpperCase() === PUNCHOUT_CONFIG.MALARENERGI.COMPANY_NAME) {
    const { name } = [...product.breadcrumb.parents].pop();
    return name;
  }

  return DEFAULT_UNSPCS_CODE;
}

/**
 * NOTE:
 * Company specific requirements
 *
 * 2. PEAB: if "STK" return "ST"
 */
export function formatUnit(companyName = '', unit: string) {
  if (companyName.toUpperCase() === PUNCHOUT_CONFIG.PEAB.COMPANY_NAME) {
    if (unit?.toUpperCase() === STYCK_UNIT) {
      return PUNCHOUT_CONFIG.PEAB.STYCK_UNIT_FORMAT;
    }
  }

  return unit?.toUpperCase();
}

export function findSSGNumber(attributes: ProductAttribute[]) {
  const result = attributes.find((attr) => attr.attributeName.toUpperCase() === 'SSG');

  return result?.attributeValue || '';
}

export function calculateLeadTime(stock: Stock, centralWarehoueId: string) {
  const defaultLeadtime = '1';
  const centralWarehouseStock = stock?.locations?.find((location) => location?.locationId === centralWarehoueId);
  if (centralWarehouseStock) {
    return centralWarehouseStock.availableToDays || defaultLeadtime;
  }

  return defaultLeadtime;
}

export function createPunchoutForm({
  companyName,
  centralWarehouseId,
  items,
}: {
  companyName: string;
  centralWarehouseId: string;
  items: CartItem[];
}) {
  let error;
  if (items.filter((item) => !item.price).length) error = 'price';

  const data = items.reduce((obj, item, currentIndex) => {
    const index = currentIndex + 1;
    obj[`NEW_ITEM-DESCRIPTION[${index}]`] = [item.name];
    obj[`NEW_ITEM-VENDORMAT[${index}]`] = [item.articleNumber];
    obj[`NEW_ITEM-QUANTITY[${index}]`] = [item.quantity];
    obj[`NEW_ITEM-CURRENCY[${index}]`] = ['SEK'];
    obj[`NEW_ITEM-PRICEUNIT[${index}]`] = ['1'];
    obj[`NEW_ITEM-PRICE[${index}]`] = [item.price?.netPrice ?? '0.00'];
    obj[`NEW_ITEM-EXT_PRODUCT_ID[${index}]`] = [findSSGNumber(item.attributes)];
    obj[`NEW_ITEM-LEADTIME[${index}]`] = [calculateLeadTime(item.stock, centralWarehouseId)];
    obj[`NEW_ITEM-UNIT[${index}]`] = [formatUnit(companyName, item.price?.salesPriceUnit)];
    obj[`NEW_ITEM-CUST_FIELD1[${index}]`] = [findUNSPSCCode(companyName)];
    obj[`NEW_ITEM-MATGROUP[${index}]`] = [findMaterialGroup(companyName, item.product)];
    obj[`NEW_ITEM-CUST_FIELD3[${index}]`] = [''];
    return obj;
  }, {});
  return { data, error };
}

export function createPunchoutSettings(data: {
  punchout: {
    companyName: string;
    callback_url: string;
  };
  HOOK_URL: string;
  Bolag: string;
  email: string;
  logonId: string;
  oci: string;
}) {
  const timestamp = Date.now();
  return {
    ...data,
    last_used: timestamp,
  };
}

const logonIdKeys = ['logonId', 'USER_NAME', 'USERNAME', 'username'];
const logonPasswordKeys = ['logonPassword', 'PASSWORD', 'password'];
const emailKeys = ['email', 'SENDER_ID'];

const getPunchoutValue = (keys: string[], query: LocationQuery) => {
  const key = keys.find((k) => query[k]);
  return key !== undefined ? (query[key] as string) : undefined;
};

const getPunchoutEmailValue = (query: LocationQuery): string | undefined => {
  const emailParameter = getPunchoutValue(emailKeys, query);

  // Special case for Visma Proceedo users
  const isUserSessionUsed = query.user_session;
  if (isUserSessionUsed) {
    try {
      const userSessionObject = JSON.parse(query.user_session as string);
      return userSessionObject?.User?.UserID;
    } catch {
      // eslint-disable-next-line no-console
      console.warn('Incorrect user session object passed ', query.user_session);
      return emailParameter;
    }
  }

  return emailParameter;
};

export function parsePunchoutQueryParameters(query: LocationQuery): Record<string, string | undefined> {
  return {
    ...(query as Record<string, string>),
    logonId: getPunchoutValue(logonIdKeys, query),
    logonPassword: getPunchoutValue(logonPasswordKeys, query),
    email: getPunchoutEmailValue(query),
  };
}
